import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { listFaqs } from "../lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SkeletonCard } from "../components/shared/Skeleton";
import EmptyState from "../components/shared/EmptyState";
import ErrorBanner from "../components/shared/ErrorBanner";
import { t } from "../lib/i18n";

export default function FAQs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await listFaqs();
        if (!cancelled) setFaqs(data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = !q
      ? faqs
      : faqs.filter(
          (f) =>
            (f.question || "").toLowerCase().includes(q) ||
            (f.answer || "").toLowerCase().includes(q),
        );
    return filtered.reduce((acc, f) => {
      const key = f.category || "general";
      acc[key] = acc[key] || [];
      acc[key].push(f);
      return acc;
    }, {});
  }, [faqs, query]);

  const groupKeys = Object.keys(groups);

  return (
    <section
      data-testid="faqs-page"
      className="mx-auto max-w-3xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28"
    >
      <p className="text-xs uppercase tracking-[0.32em] text-[#C8A24B]">
        {t("brand.name")}
      </p>
      <h1 className="mt-3 font-display text-4xl text-[#1A1A1A] sm:text-5xl">
        {t("faqs.title")}
      </h1>
      <p className="mt-4 text-base text-[#1A1A1A]/65">
        {t("faqs.subtitle")}
      </p>

      <div className="mt-10 flex items-center gap-3 rounded-full border border-[#EAE3D2] bg-white px-5 py-3">
        <Search className="h-4 w-4 text-[#1A1A1A]/45" />
        <input
          type="text"
          data-testid="faqs-search-input"
          placeholder={t("faqs.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 bg-transparent text-sm text-[#1A1A1A] placeholder-[#1A1A1A]/40 outline-none"
        />
      </div>

      <div className="mt-10">
        {loading ? (
          <ul className="space-y-4">
            {[0, 1, 2].map((i) => (
              <li key={i}>
                <SkeletonCard />
              </li>
            ))}
          </ul>
        ) : error ? (
          <ErrorBanner onRetry={() => setRetryKey((k) => k + 1)} />
        ) : !groupKeys.length ? (
          <EmptyState title={t("faqs.empty")} testid="faqs-empty" />
        ) : (
          <div className="space-y-12">
            {groupKeys.map((cat) => (
              <section key={cat} data-testid={`faqs-group-${cat}`}>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#1A1A1A]/45">
                  {cat}
                </p>
                <Accordion type="single" collapsible className="mt-3">
                  {groups[cat].map((q) => (
                    <AccordionItem
                      key={q.id}
                      value={q.id}
                      data-testid={`faq-item-${q.id}`}
                      className="border-[#EAE3D2]"
                    >
                      <AccordionTrigger className="text-left text-base text-[#1A1A1A]">
                        {q.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#1A1A1A]/75">
                        {q.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
